/**
 * Form Utilities
 *
 * Client-side validation and submit-lifecycle UX for forms.
 * Used by section scripts (e.g., share-your-work-form, wholesale-form).
 *
 * Conventions in markup:
 *   - <input data-validate="email,min:2"> — comma-separated rule list
 *   - <input required> — implicit `required` rule (a checkbox/radio must be checked)
 *   - <input data-error-email="..." data-error-required="..."> — per-rule message
 *   - <p data-form-error="<input-name>"> — error sink for that field
 *
 * Shopify's spam protection (storefront-recaptcha-v3) attaches an inline
 * `onsubmit` to every contact form that fetches a reCAPTCHA token and then
 * submits the form natively. Our submit listener is registered in the capture
 * phase so it always runs first, and when it handles the submit itself (ajax)
 * it stops propagation so Shopify's handler never fires a second POST.
 */

const RECAPTCHA_TIMEOUT_MS = 5000;

const VALIDATORS = {
  required: (value) => value.trim().length > 0,
  email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()),
  tel: (value) => /^[+\d][\d\s\-().]{5,}$/.test(value.trim()),
  url: (value) => /^https?:\/\/\S+$/i.test(value.trim()),
};

const PARAMETRIC = {
  min: (value, n) => value.trim().length >= Number(n),
  max: (value, n) => value.trim().length <= Number(n),
};

const isCheckable = (field) => field.type === 'checkbox' || field.type === 'radio';

export class FormValidator {
  constructor(form, options = {}) {
    this.form = form;
    this.fields = Array.from(form.querySelectorAll('[data-validate], [required]'));
    this.submitButton = form.querySelector('[type="submit"]');

    // When ajax is enabled, a valid form is submitted over fetch and the
    // server-rendered success/error notice is shown inline (no page reload).
    this.ajax = options.ajax || false;
    this.successSelector = options.successSelector || '.form-notice--success';
    this.errorSelector = options.errorSelector || '.form-notice--error';

    this.onSubmit = this.onSubmit.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onChange = this.onChange.bind(this);
    this.nativeHandoff = false;
  }

  init() {
    this.form.setAttribute('novalidate', '');
    this.form.addEventListener('submit', this.onSubmit, true);
    this.fields.forEach((field) => {
      if (isCheckable(field)) {
        field.addEventListener('change', this.onChange);
        return;
      }
      field.addEventListener('blur', this.onBlur);
      field.addEventListener('input', this.onInput);
    });
  }

  destroy() {
    this.form.removeEventListener('submit', this.onSubmit, true);
    this.fields.forEach((field) => {
      field.removeEventListener('change', this.onChange);
      field.removeEventListener('blur', this.onBlur);
      field.removeEventListener('input', this.onInput);
    });
  }

  onBlur(event) {
    this.validateField(event.currentTarget);
  }

  onInput(event) {
    if (event.currentTarget.getAttribute('aria-invalid') === 'true') {
      this.clearError(event.currentTarget);
    }
  }

  // Checkboxes and radios are validated as soon as they change, so the
  // consent error disappears the moment the box is ticked.
  onChange(event) {
    this.validateField(event.currentTarget);
  }

  onSubmit(event) {
    if (!this.validateAll()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const firstInvalid = this.form.querySelector('[aria-invalid="true"]');
      if (firstInvalid) {
        this.scrollToElement(firstInvalid);
        firstInvalid.focus({ preventScroll: true });
      }
      return;
    }
    this.setBusy(true);
    this.form.dispatchEvent(new CustomEvent('form:submitting', { bubbles: true }));

    const handoff = this.nativeHandoff;
    this.nativeHandoff = false;

    // `defaultPrevented` means another handler already owns this submit
    // (e.g. Shopify's reCAPTCHA hook on a browser that runs it first); posting
    // again over fetch would send the message twice.
    if (this.ajax && !handoff && !event.defaultPrevented) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitAjax();
    }
  }

  async submitAjax() {
    const form = this.form;
    try {
      const body = new FormData(form);
      const token = await this.getRecaptchaToken();
      if (token) body.set('recaptcha-v3-token', token);

      const response = await fetch(form.action, {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const success = doc.querySelector(this.successSelector);
      const error = doc.querySelector(this.errorSelector);
      const slot = form.querySelector('[data-form-notice]');

      const place = (source) => {
        const notice = source.cloneNode(true);
        if (slot) {
          slot.replaceChildren(notice);
        } else {
          form.appendChild(notice);
        }
        this.scrollToElement(notice);
      };

      if (success) {
        // Leave the submit button disabled so the form isn't resubmitted.
        place(success);
      } else if (error) {
        this.setBusy(false);
        place(error);
      } else if (/contact_posted=true/.test(response.url)) {
        // Shopify accepted the message but the redirect target didn't carry
        // our notice markup: show the server-rendered page instead of posting
        // again.
        window.location.assign(response.url);
      } else {
        // Most likely Shopify's "/challenge" captcha page. Hand the submit to
        // the normal (non-ajax) flow so Shopify's own hook can run it.
        this.submitNative();
      }
    } catch (_) {
      this.submitNative();
    }
  }

  // Re-dispatches a real submit event (unlike form.submit()) so Shopify's
  // reCAPTCHA hook still gets to add its token; onSubmit skips ajax once.
  submitNative() {
    this.nativeHandoff = true;
    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
    } else {
      this.form.submit();
    }
  }

  // Resolves with a reCAPTCHA v3 token when Shopify's spam protection has
  // loaded grecaptcha on the page, otherwise null (the store has it off).
  getRecaptchaToken() {
    const siteKey = window.Shopify?.recaptchaV3?.siteKey;
    const grecaptcha = window.grecaptcha;
    if (!siteKey || !grecaptcha || typeof grecaptcha.execute !== 'function') {
      return Promise.resolve(null);
    }
    const action = this.form.querySelector('input[name="form_type"]')?.value || 'contact';

    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), RECAPTCHA_TIMEOUT_MS);
      const done = (token) => {
        clearTimeout(timer);
        resolve(token || null);
      };
      const run = () => {
        try {
          grecaptcha.execute(siteKey, { action }).then(done, () => done(null));
        } catch (_) {
          done(null);
        }
      };
      if (typeof grecaptcha.ready === 'function') grecaptcha.ready(run);
      else run();
    });
  }

  scrollToElement(element) {
    requestAnimationFrame(() => {
      if (window.lenis && typeof window.lenis.scrollTo === 'function') {
        window.lenis.scrollTo(element, { offset: -80, duration: 1.0 });
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  validateAll() {
    return this.fields.reduce((acc, field) => this.validateField(field) && acc, true);
  }

  validateField(field) {
    // A checkbox/radio always carries its `value` attribute, so "has a value"
    // must mean "is checked" for it (the consent box used to pass unticked).
    const value = isCheckable(field) ? (field.checked ? field.value || 'on' : '') : field.value || '';
    const rules = this.getRules(field);

    for (const rule of rules) {
      const [name, arg] = rule.split(':');
      const isOptional = name !== 'required' && value.trim().length === 0;
      if (isOptional) continue;

      const fn = arg !== undefined ? PARAMETRIC[name] : VALIDATORS[name];
      if (!fn) continue;

      const isValid = arg !== undefined ? fn(value, arg) : fn(value);
      if (!isValid) {
        this.setError(field, name);
        return false;
      }
    }

    this.clearError(field);
    return true;
  }

  getRules(field) {
    const rules = [];
    if (field.hasAttribute('required')) rules.push('required');
    const dataRules = field.dataset.validate;
    if (dataRules) {
      dataRules.split(',').forEach((raw) => {
        const rule = raw.trim();
        if (rule && !rules.includes(rule)) rules.push(rule);
      });
    }
    return rules;
  }

  setError(field, ruleName) {
    const datasetKey = `error${ruleName.charAt(0).toUpperCase()}${ruleName.slice(1)}`;
    const message = field.dataset[datasetKey] || field.dataset.errorDefault || ruleName;
    field.setAttribute('aria-invalid', 'true');

    const errorEl = this.findErrorElement(field);
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;

    if (errorEl.id) {
      const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!describedBy.includes(errorEl.id)) {
        describedBy.push(errorEl.id);
        field.setAttribute('aria-describedby', describedBy.join(' '));
      }
    }
  }

  clearError(field) {
    field.removeAttribute('aria-invalid');
    const errorEl = this.findErrorElement(field);
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  findErrorElement(field) {
    if (!field.name) return null;
    return this.form.querySelector(`[data-form-error="${CSS.escape(field.name)}"]`);
  }

  setBusy(isBusy) {
    if (this.submitButton) {
      this.submitButton.disabled = isBusy;
      this.submitButton.setAttribute('aria-busy', String(isBusy));
    }
    this.form.classList.toggle('is-submitting', isBusy);
  }
}
